import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Users, Globe } from 'lucide-react';
import { requireLevel } from '@/lib/guards';
import { useCompany } from '@/lib/company-context';
import { useCompanyMembers, useCompanyDomains } from '@/lib/queries/corporate';
import { EmptyState } from '@/components/shared/empty-state';
import { LevelBadge } from '@/components/shared/badge';
import { Skeleton } from '@/components/shared/skeleton';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/app/team/')({
  beforeLoad: requireLevel(5),
  component: TeamPage,
});

type Tab = 'members' | 'domains';

function TeamPage() {
  const { level } = useCompany();
  const [tab, setTab] = useState<Tab>('members');

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-foreground">Team</h1>

      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        <button
          type="button"
          onClick={() => setTab('members')}
          className={cn(
            'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
            tab === 'members'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Members
        </button>
        {level >= 7 && (
          <button
            type="button"
            onClick={() => setTab('domains')}
            className={cn(
              'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
              tab === 'domains'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Domains
          </button>
        )}
      </div>

      {tab === 'members' ? <MembersTab /> : <DomainsTab />}
    </div>
  );
}

function MembersTab() {
  const { company } = useCompany();
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading } = useCompanyMembers(company.slug, limit, page * limit);

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : !data?.members?.length ? (
        <EmptyState icon={Users} title="No members found" />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {data.members.map(
            (m: {
              user_id: string;
              user_display_name?: string;
              user_avatar_url?: string;
              level: number;
              designation_id?: string;
            }) => (
              <Link
                key={m.user_id}
                to="/app/team/$userId"
                params={{ userId: m.user_id }}
                className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                  {m.user_avatar_url ? (
                    <img
                      src={m.user_avatar_url}
                      alt=""
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">
                      {(m.user_display_name ?? 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {m.user_display_name ?? 'Unknown'}
                  </p>
                </div>
                <LevelBadge level={m.level} />
              </Link>
            )
          )}
        </div>
      )}

      {data && data.total > limit && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-accent disabled:opacity-40 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {Math.ceil(data.total / limit)}
          </span>
          <button
            type="button"
            disabled={(page + 1) * limit >= data.total}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-accent disabled:opacity-40 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function DomainsTab() {
  const { company } = useCompany();
  const { data, isLoading } = useCompanyDomains(company.slug);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (!data?.domains?.length) {
    return (
      <EmptyState
        icon={Globe}
        title="No domains"
        description="No email domains are configured for this company."
      />
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card divide-y divide-border">
      {data.domains.map(
        (d: { id: string; domain: string; is_primary: boolean; verified: boolean }) => (
          <div key={d.id} className="flex items-center gap-3 px-4 py-3">
            <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="flex-1 text-sm text-foreground">{d.domain}</span>
            {d.is_primary && <span className="text-xs font-medium text-primary">Primary</span>}
            <span
              className={cn(
                'text-xs font-medium',
                d.verified
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-amber-600 dark:text-amber-400'
              )}
            >
              {d.verified ? 'Verified' : 'Pending'}
            </span>
          </div>
        )
      )}
    </div>
  );
}
